import { useCallback } from 'react';
import { TransactionFormData } from '@/types';
import { validateForm, transactionSchema } from '@/utils/validations';

interface UseTransactionFormHandlersProps {
  formData: TransactionFormData;
  setFormData: (updater: (prev: TransactionFormData) => TransactionFormData) => void;
  errors: Record<string, string>;
  setErrors: (updater: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  clearFieldError: (field: string) => void;
  debouncedValidateAmount: (value: string) => void;
  setAmountString: (value: string) => void;
  onSubmit: (data: any) => void;
}

export const useTransactionFormHandlers = ({
  formData,
  setFormData,
  errors,
  setErrors,
  clearFieldError,
  debouncedValidateAmount,
  setAmountString,
  onSubmit
}: UseTransactionFormHandlersProps) => {
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('TransactionForm - Submitting form data:', formData);
    console.log('TransactionForm - Date being submitted:', formData.date);
    
    const validation = validateForm(transactionSchema, formData);
    
    if (!validation.success) {
      console.log('TransactionForm - Validation errors:', validation.errors);
      setErrors(validation.errors || {});
      return;
    }
    
    // Additional validation for installments
    if (formData.isInstallment) {
      if (!formData.installments || formData.installments < 2 || formData.installments > 24) {
        setErrors(prev => ({ ...prev, installments: 'Número de parcelas deve ser entre 2 e 24' }));
        return;
      }
    }
    
    console.log('TransactionForm - Validation passed, calling onSubmit');
    setErrors({});
    onSubmit(formData);
  }, [formData, onSubmit, setErrors]);

  const handleInputChange = useCallback((field: keyof TransactionFormData, value: any) => {
    console.log(`TransactionForm - Field ${field} changed to:`, value);
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      clearFieldError(field);
    }
  }, [errors, setFormData, clearFieldError]);

  const handleAmountChange = useCallback((value: string) => {
    setAmountString(value);
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      handleInputChange('amount', numericValue);
      debouncedValidateAmount(value);
    }
  }, [handleInputChange, debouncedValidateAmount, setAmountString]);

  const handleTypeChange = useCallback((value: string) => {
    setFormData(prev => ({
      ...prev,
      type: value as 'receita' | 'despesa',
      categoryId: '', // Clear category when type changes
      // Reset cardId when switching to income to avoid validation issues
      cardId: value === 'receita' && prev.paymentMethod === 'cartao' ? '' : prev.cardId
    }));
    
    if (errors.type) {
      clearFieldError('type');
    }
  }, [errors.type, setFormData, clearFieldError]);

  return {
    handleSubmit,
    handleInputChange,
    handleAmountChange,
    handleTypeChange
  };
};

// Export as default export for compatibility
export default useTransactionFormHandlers;